import { Avatar, Box, Tab, Tabs } from "@mui/material";
import React, { useCallback, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { BreadcrumbAdmin } from "../../../../UI/BreadcrumbAdmin";
import DefaultMaleProfilePic from "../../../../../assets/Icons/profile/Profile icon _ male.png";
import { useGetEmployeeByIdQuery } from "../../../../../redux-toolkit/features/employee/employeeApiSlice";
import ProgressBar from "../../../../UI/ProgressBar";
import { Error404 } from "../../../../UI/404/Error404";
import { EmployeeActivityTasks } from "./EmployeeActivityTasks";
import { EmployeeActivityTimeTrails } from "./EmployeeActivityTimeTrails";
import { EmployeeActivityRewards } from "./EmployeeActivityRewards";
import "../../../../Admin/Task Verification/Task Detailed/TaskDetailed.css";
import "./EmployeeActivityPage.css";

const sectionTabSx = {
  textTransform: "capitalize",
  fontWeight: "500",
  fontSize: "15px",
};

const isObjectIdLike = (value) =>
  typeof value === "string" && /^[a-f\d]{24}$/i.test(value.trim());

/** Company can arrive populated, as a bare id, or as a plain name string. */
const resolveCompanyName = (...candidates) => {
  for (const candidate of candidates) {
    if (candidate && typeof candidate === "object" && candidate.name) {
      return candidate.name;
    }
    if (typeof candidate === "string" && candidate.trim() && !isObjectIdLike(candidate)) {
      return candidate.trim();
    }
  }
  return "";
};

export const EmployeeActivityPage = () => {
  const { employeeId, company } = useParams();
  const location = useLocation();
  const role = useSelector((state) => state.auth.user?.role);
  const allowedAccess =
    useSelector((state) => state.auth.user?.allowedAccess) || [];

  const isAdminPath = location.pathname.includes("/admin/manage-company/");
  const isSuperAdminView =
    !isAdminPath &&
    (role === "SUPERADMIN" ||
      (role === "CUSTOM_ROLE" &&
        allowedAccess.includes("SUPERADMIN_EMPLOYEES")));

  const sectionOptions = useMemo(() => {
    if (isSuperAdminView) {
      return [
        { key: "tasks", label: "Tasks" },
        { key: "time-trails", label: "Time Trails" },
        { key: "rewards", label: "Rewards" },
      ];
    }
    return [{ key: "tasks", label: "Tasks" }];
  }, [isSuperAdminView]);

  const [activeSection, setActiveSection] = useState("tasks");
  const [taskUserSummary, setTaskUserSummary] = useState(null);

  const {
    data: employeeResponse,
    isLoading: isEmployeeLoading,
  } = useGetEmployeeByIdQuery(employeeId, {
    skip: !employeeId,
    refetchOnMountOrArgChange: true,
  });

  const employee = employeeResponse?.data || {};
  const handleTaskSummaryChange = useCallback((user) => {
    setTaskUserSummary(user);
  }, []);

  const displayName =
    `${employee?.fname || taskUserSummary?.fname || ""} ${
      employee?.lname || taskUserSummary?.lname || ""
    }`.trim() || "Employee";
  const companyName = resolveCompanyName(
    employee?.company,
    taskUserSummary?.company
  );
  const avatarSrc =
    employee?.avatar || taskUserSummary?.avatar || DefaultMaleProfilePic;

  const employeesListPath = isAdminPath
    ? `/admin/manage-company/employee/${company}`
    : "/super-admin/manage-organization/employee";

  const breadcrumbBase = [
    {
      _id: 1,
      link: employeesListPath,
      label: "Employees",
    },
  ];

  const activeSectionIndex = Math.max(
    0,
    sectionOptions.findIndex((section) => section.key === activeSection)
  );

  if (isEmployeeLoading && !taskUserSummary) {
    return <ProgressBar />;
  }

  if (!employeeId) {
    return <Error404 />;
  }

  return (
    <div className="container employeeActivityPage">
      <BreadcrumbAdmin base={breadcrumbBase} page="Activity" />

      <div className="taskdetHead employeeActivityPage__header">
        <div className="task__head">
          <div className="task__headprofile">
            <Avatar
              alt={displayName}
              sx={{ width: "65px", height: "65px" }}
              src={avatarSrc}
            />
          </div>
          <div>
            <div>
              <h2>{displayName}</h2>
            </div>
            {companyName ? (
              <div>
                <h4 className="ftwt__r">{companyName}</h4>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {isSuperAdminView ? (
        <Box
          className="employeeActivityPage__sectionTabs"
          sx={{ width: "100%" }}
        >
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={activeSectionIndex}
              onChange={(_event, newValue) =>
                setActiveSection(sectionOptions[newValue]?.key || "tasks")
              }
              aria-label="employee activity sections"
            >
              {sectionOptions.map((section) => (
                <Tab
                  key={section.key}
                  label={section.label}
                  sx={sectionTabSx}
                />
              ))}
            </Tabs>
          </Box>
        </Box>
      ) : null}

      {activeSection === "tasks" ? (
        <EmployeeActivityTasks
          employeeId={employeeId}
          onSummaryChange={handleTaskSummaryChange}
        />
      ) : null}
      {activeSection === "time-trails" && isSuperAdminView ? (
        <EmployeeActivityTimeTrails employeeId={employeeId} />
      ) : null}
      {activeSection === "rewards" && isSuperAdminView ? (
        <EmployeeActivityRewards employeeId={employeeId} />
      ) : null}
    </div>
  );
};
